/** Data hooks over the local tables. */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { get } from "./api";
import { bySortKey } from "./order";
import { mergeRows, useRows, useTable } from "./store";
import { sync } from "./sync";
import type { Cycle, Id, Issue, Me, Team, WorkflowState } from "./types";

export function useMe() {
  return useQuery({ queryKey: ["me"], queryFn: () => get<Me>("/api/me"), staleTime: 60_000, retry: false });
}

export function useMeId(): Id | null {
  const me = useMe().data;
  return me?.user.id ?? sync.me;
}

export function useRole() {
  return useMe().data?.membership?.role ?? "member";
}

export function useIsAdmin() {
  const r = useRole();
  return r === "owner" || r === "admin";
}

export function useTeams(): Team[] {
  return useRows("team", undefined, (a, b) => a.name.localeCompare(b.name));
}

/** Teams the person belongs to first, then the rest. */
export function useMyTeams(): Team[] {
  const me = useMeId();
  const teams = useTeams();
  return useMemo(() => teams.filter((t) => me && t.memberIds.includes(me)), [teams, me]);
}

export function useTeamByKey(key: string | undefined): Team | undefined {
  const teams = useTable("team");
  return useMemo(() => Object.values(teams).find((t) => t.key === key?.toUpperCase()), [teams, key]);
}

export function useStates(teamId: Id | null | undefined): WorkflowState[] {
  return useRows("workflowState", (s) => s.teamId === teamId, (a, b) => a.position - b.position, [teamId]);
}

export function useUsers() {
  return useRows("user", (u) => u.active, (a, b) => a.name.localeCompare(b.name));
}

/** Labels usable on an issue in this team: workspace labels plus the team's. */
export function useLabelsFor(teamId: Id | null | undefined, scope: "issue" | "project" | "idea" = "issue") {
  return useRows("label", (l) => (l.teamId == null || l.teamId === teamId) && (l.scope === scope || scope === "issue"), (a, b) => a.name.localeCompare(b.name), [teamId, scope]);
}

export function useCycles(teamId: Id | null | undefined): Cycle[] {
  return useRows("cycle", (c) => c.teamId === teamId, (a, b) => a.startsOn.localeCompare(b.startsOn), [teamId]);
}

export function currentCycle(cycles: Cycle[]): Cycle | undefined {
  const today = new Date().toISOString().slice(0, 10);
  return cycles.find((c) => c.startsOn <= today && c.endsOn > today);
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuid = (s: string) => UUID.test(s);

/** Finds an issue by identifier (ENG-12) or by id, which links from webhooks and exports use. */
export function useIssueByIdentifier(ident: string | undefined): Issue | undefined {
  const issues = useTable("issue");
  const teams = useTable("team");
  return useMemo(() => {
    if (ident && isUuid(ident)) return issues[ident.toLowerCase()];
    const m = ident ? /^([A-Za-z][A-Za-z0-9]{0,6})-(\d+)$/.exec(ident) : null;
    if (!m) return undefined;
    const team = Object.values(teams).find((t) => t.key === m[1].toUpperCase());
    if (!team) return undefined;
    return Object.values(issues).find((i) => i.teamId === team.id && i.number === Number(m[2]));
  }, [issues, teams, ident]);
}

export function useSubIssues(parentId: Id | undefined) {
  return useRows("issue", (i) => i.parentId === parentId, bySortKey, [parentId]);
}

/** Comments and reactions for one parent, fetched once then kept live by deltas. */
export function useComments(parent: { issueId?: Id; ideaId?: Id; documentId?: Id; projectUpdateId?: Id; projectId?: Id }) {
  const [field, id] = Object.entries(parent).find(([, v]) => v) ?? [];
  useQuery({
    queryKey: ["lazy", "comments", field, id],
    enabled: !!id,
    queryFn: async () => {
      const res = await get<{ comment: never[]; reaction: never[] }>(`/api/comments?${field}=${id}`);
      mergeRows("comment", res.comment);
      mergeRows("reaction", res.reaction);
      return true;
    },
  });
  return useRows("comment", (c) => !!field && (c as unknown as Record<string, unknown>)[field] === id, (a, b) => a.createdAt.localeCompare(b.createdAt), [field, id]);
}

export function useNotificationsLoaded() {
  return useQuery({
    queryKey: ["lazy", "notifications"],
    queryFn: async () => {
      const res = await get<{ notification: never[] }>("/api/notifications");
      mergeRows("notification", res.notification);
      return true;
    },
    staleTime: Infinity,
  });
}

export function useUnreadCount() {
  useNotificationsLoaded();
  const me = useMeId();
  const rows = useRows("notification", (n) => n.userId === me && !n.readAt && (!n.snoozedUntil || n.snoozedUntil < new Date().toISOString()), undefined, [me]);
  return rows.length;
}
